import React, { useRef, useCallback, useEffect } from 'react';
import { snapshot_ChartHeaderButtons as ChartHeaderButtons } from './snapshot_ChartHeaderButtons';
import useResizeObserver from '@/hooks/useResizeObserver';
import { useSnapshotRow } from '../contexts/SnapshotRowContext';
import { useShareUrl } from '@/hooks/useShareUrl';
import { snapshotMetricDefs } from '../data/SnapshotAboutTheDataDefs';

interface MetricCardProps {
  title: string;
  description: string;
  changePercent: string;
  changeDescription: string;
  changeType: 'positive' | 'negative';
  value: string;
  valueUnit: string;
  valueUnitDescription?: string;
  className?: string;
  rowIndex: number;
  cardIndex: number;
  chartName?: string;
  shareLink?: string;
}

export const snapshot_MetricCard: React.FC<MetricCardProps> = ({
  title,
  description,
  changePercent,
  changeDescription,
  changeType,
  value,
  valueUnit,
  valueUnitDescription,
  className = "",
  rowIndex,
  cardIndex,
  chartName,
  shareLink
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { rowHeights, headerHeights, updateHeight, updateHeaderHeight, removeCard } = useSnapshotRow();
  const { height: headerHeight } = useResizeObserver(headerRef);
  const { height: contentHeight } = useResizeObserver(contentRef);
  
  // Use provided shareLink or fall back to current URL
  const currentShareLink = useShareUrl();
  const finalShareLink = shareLink || currentShareLink;
  const finalChartName = chartName || title;
  const metricDef = snapshotMetricDefs[finalChartName as keyof typeof snapshotMetricDefs];
  
  useEffect(() => {
    updateHeight(rowIndex, cardIndex, headerHeight);
  }, [updateHeight, rowIndex, cardIndex, headerHeight]);

  useEffect(() => {
    updateHeaderHeight(rowIndex, cardIndex, contentHeight);
  }, [updateHeaderHeight, rowIndex, cardIndex, contentHeight]);

  useEffect(() => {
    // Cleanup function
    return () => {
      removeCard(rowIndex, cardIndex);
    };
  }, [removeCard, rowIndex, cardIndex]);

  // Get the maximum heights for this row
  const rowMaxHeight = rowHeights.get(rowIndex) || 0;
  const headerMaxHeight = headerHeights.get(rowIndex) || 0;

  const tickerStyles = changeType === 'positive' 
    ? "text-[#4B7400] bg-[#F3FAE5]" 
    : "text-[#B30000] bg-[#FFDADA]";

  return (
    <article className={`bg-white w-full min-w-[280px] rounded-[20px] relative flex flex-col h-full ${className}`}>
      <div className="pt-6">
        <ChartHeaderButtons chartName={finalChartName} shareLink={finalShareLink} metricDefinition={metricDef} aboutDescription={description} />
      </div>
      <header 
        ref={headerRef} 
        className="w-full px-6 relative pb-6 max-md:max-w-full max-md:px-5 flex-shrink-0 flex flex-col after:content-[''] after:absolute after:bottom-0 after:inset-x-6 after:max-md:inset-x-5 after:h-[4px] after:bg-gradient-to-r after:from-[#CA0079] after:to-[#F1AB11] after:rounded-full mt-6"
        style={{ 
          minHeight: `${rowMaxHeight}px`,
          transition: 'min-height 0.2s ease-in-out'
        }}
      >
        <div 
          ref={contentRef}
          className="w-full mt-2"
          style={{
            minHeight: `${headerMaxHeight}px`,
            transition: 'min-height 0.2s ease-in-out'
          }}
        >
          <h2 className="text-[#212121] text-[28px] font-semibold leading-tight align-middle flex-1 chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
            {title}
          </h2>
          <p className="text-[#212121] text-xl font-normal leading-tight align-middle mt-3 chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
            {description}. Brought to you by Govstream.ai.
          </p>
        </div>
      </header>
      <div className="flex w-full flex-col items-stretch whitespace-nowrap px-6 pb-4 flex-grow justify-end max-md:px-5">
        <div className="flex items-center gap-2 text-sm font-medium leading-tight align-middle mt-20 chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
          <div className={`justify-center items-center self-stretch flex my-auto px-3 py-1.5 rounded-[20px] ${tickerStyles}`}>
            <span className="flex items-center gap-1">
              {changePercent !== '0.0%' && (
                <span 
                  className={`material-symbols-outlined text-sm transition-transform duration-200 ${changeType === 'negative' ? 'rotate-180' : ''}`}
                  style={{ fontSize: '16px' }}
                >
                  arrow_upward_alt
                </span>
              )}
              <span className="self-stretch my-auto text-sm">
                {changePercent === '0.0%' ? 'No data' : changePercent.startsWith('-') ? changePercent.slice(1) : changePercent}
              </span>
            </span>
          </div>
          <span className="text-[#212121] self-stretch my-auto opacity-70 leading-tight align-middle chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
            {changePercent === '0.0%' ? '' : changeDescription}
          </span>
        </div>
        <div className="flex w-full gap-2.5 mt-2 items-baseline">
          <span className="text-[#212121] text-[48px] font-bold leading-tight align-middle max-md:text-[48px] chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
            {value}
          </span>
          <span className="text-[#212121] text-xl font-medium leading-tight align-middle opacity-70 chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
            {valueUnitDescription || valueUnit}
          </span>
        </div>
      </div>
    </article>
  );
};
